/**
 * The timeline, drawn with ECharts.
 *
 * A claim with an asserted validity period is drawn as a bar; one without is drawn as a
 * point at its observation time and **labelled as undated**. The distinction is the
 * whole reason `has_asserted_period` is in the payload: "employed here from 2019 to
 * 2023" and "we saw this in 2024" are different claims, and a chart that drew them the
 * same way would invent a precision the evidence does not have.
 */

import { useEffect, useMemo, useRef, type ReactElement } from 'react';
import * as echarts from 'echarts';
import { useQuery } from '@tanstack/react-query';
import { request } from '@/lib/api';
import type { TimelineItem, TimelineResponse } from '@/lib/schemas';
import { ConfidenceBadge, ErrorState, Loading, ProvenanceLink, Table } from '@/components/ui';

/** The timeline tab for one case. */
export function TimelineTab({ caseId }: { caseId: string }): ReactElement {
  const container = useRef<HTMLDivElement | null>(null);

  const timeline = useQuery({
    queryKey: ['timeline', caseId],
    queryFn: () => request<TimelineResponse>(`/cases/${caseId}/timeline`),
  });

  const items = useMemo(() => timeline.data?.items ?? [], [timeline.data]);

  useEffect(() => {
    if (!container.current || items.length === 0) return undefined;

    const chart = echarts.init(container.current, null, { renderer: 'canvas' });
    const dated = items.filter((item) => item.has_asserted_period);
    const undated = items.filter((item) => !item.has_asserted_period);

    chart.setOption({
      backgroundColor: 'transparent',
      tooltip: { trigger: 'item' },
      grid: { left: 160, right: 24, top: 24, bottom: 40 },
      xAxis: { type: 'time' },
      yAxis: {
        type: 'category',
        data: items.map((item) => `${item.predicate}: ${item.object_value}`.slice(0, 40)),
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          name: 'Asserted period',
          type: 'custom',
          renderItem: renderInterval,
          encode: { x: [1, 2], y: 0 },
          data: dated.map((item) => [
            indexOf(items, item),
            new Date(item.valid_from ?? item.observed_at).getTime(),
            new Date(item.valid_to ?? item.observed_at).getTime(),
          ]),
        },
        {
          name: 'Observed only',
          type: 'scatter',
          symbolSize: 8,
          data: undated.map((item) => [
            new Date(item.observed_at).getTime(),
            indexOf(items, item),
          ]),
        },
      ],
    });

    const resize = (): void => chart.resize();
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
      chart.dispose();
    };
  }, [items]);

  if (timeline.isLoading) return <Loading />;
  if (timeline.error) return <ErrorState error={timeline.error} />;

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted">
        Bars are claims the source dated. Points are claims we only know the observation
        time for — a weaker statement, drawn differently on purpose.
      </p>
      <div
        ref={container}
        data-testid="timeline-chart"
        className="h-96 w-full rounded-lg border border-line bg-raised"
      />
      <Table
        headers={['When', 'Claim', 'Value', 'Confidence', 'Evidence']}
        empty="No dated claims yet."
        rows={items.map((item) => [
          <span key="w" className="whitespace-nowrap text-xs">
            {item.has_asserted_period
              ? `${format(item.valid_from)} – ${format(item.valid_to)}`
              : `observed ${format(item.observed_at)}`}
          </span>,
          <span key="p" className="font-mono text-xs">
            {item.predicate}
          </span>,
          item.object_value,
          <ConfidenceBadge
            key="c"
            sourceReliability={item.source_reliability}
            informationCredibility={item.information_credibility}
            extractionConfidence={item.extraction_confidence}
            compact
          />,
          <ProvenanceLink key="e" caseId={caseId} assertionId={item.assertion_id} />,
        ])}
      />
    </div>
  );
}

function indexOf(items: TimelineItem[], item: TimelineItem): number {
  return items.findIndex((candidate) => candidate.assertion_id === item.assertion_id);
}

function format(value: string | null | undefined): string {
  if (!value) return 'open';
  return new Date(value).toISOString().slice(0, 10);
}

/** Draw one validity interval as a bar. */
function renderInterval(
  params: echarts.CustomSeriesRenderItemParams,
  api: echarts.CustomSeriesRenderItemAPI,
): echarts.CustomSeriesRenderItemReturn {
  const categoryIndex = api.value(0);
  const start = api.coord([api.value(1), categoryIndex]);
  const end = api.coord([api.value(2), categoryIndex]);
  const height = 10;
  // ECharts types the coordinate system as a union across chart kinds; on a cartesian
  // grid it always carries these four. Narrowed here rather than asserted at each use.
  const coords = params.coordSys as unknown as {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  const rect = echarts.graphic.clipRectByRect(
    {
      x: start[0] ?? 0,
      y: (start[1] ?? 0) - height / 2,
      width: (end[0] ?? 0) - (start[0] ?? 0),
      height,
    },
    coords,
  );
  return rect
    ? { type: 'rect', shape: rect, style: api.style() }
    : { type: 'group', children: [] };
}
